import { chain, compact, concat, findIndex, flatten, get, has, isEmpty, set, size } from 'lodash';
import ProgramsDB from 'src/fb/programs-db';
import ProgramTargetsDB from 'src/fb/programTargets-db';
import { logEvent } from 'src/utils/LogEvent';

export default {

  addProgramTarget(payload) {
    if (!payload.target || !payload.program) {
      return;
    }
    set(this.programTargetsCache, [payload.program, payload.target.id], payload.target);
  }, // addProgramTarget()

  addProgramTargetsPerProgram(payload) {
    if (!payload.program || !payload.loadStatus) {
      return;
    }

    set(this.programTargetsPerProgramCache, payload.program, payload.loadStatus);
  }, // addProgramTargetsPerProgram()

  clearProgram({ program }) {
    if (!program) {
      return;
    }
    delete this.programTargetsCache[program];
    delete this.programTargetsPerProgramCache[program];
  }, // clearProgram()

  clearAllPrograms() {
    this.programTargetsCache = {};
    this.programTargetsPerProgramCache = {};
  }, // clearAllPrograms()

  setProperties({ programId, targetId, programTargetObj }) {
    if (!has(this.programTargetsCache, [programId, targetId])) {
      return;
    }
    Object.assign(this.programTargetsCache[programId][targetId], programTargetObj);
  }, // setProperties

  /**
   * Set array of targets to the targets cache all at once (better performance than in
   * the {@link addProgramTarget} mutation).
   *
   * @param {string} programId
   * @param {Array<Object>} targets
   * @param {boolean} [replace=false] - Replace targets in cache with provided ones
   */
  addProgramTargets({ programId, targets, replace = false }) {
    if (!programId || !(Array.isArray(targets) && targets.length > 0)) {
      return;
    }

    const groupedTargets = targets.reduce((acc, target) => {
      acc[target.id] = target;
      return acc;
    }, {});

    if (!this.programTargetsCache[programId] || replace) {
      this.programTargetsCache[programId] = groupedTargets;
      return;
    }

    // Much better in terms of performance rather than using `Object.assign()`
    this.programTargetsCache[programId] = {
      ...this.programTargetsCache[programId],
      ...groupedTargets,
    };
  }, // addProgramTargets()

  /**
   * Read Targets from cache or database. Cache the items if they are read from database
   *
   * @param {Object} ref
   * @param {string} ref.program
   * @param {string[]} [ref.targets] - if not provided, all targets by program that are in cache will be returned
   * @return {Promise<Object[]>}
   */
  async getProgramTargets(ref) {
    if (!ref) {
      return [];
    }

    if (!Array.isArray(ref.targets) || ref.targets.length === 0) {
      const targetsByProgram = get(this.programTargetsCache, [ref.program]);
      return targetsByProgram ? Object.values(targetsByProgram) : [];
    }

    const programTargetsDb = new ProgramTargetsDB(ref.program);

    const retVal = ref.targets.map(async (target) => {
      const cachedTarget = get(this.programTargetsCache, [ref.program, target]);
      if (cachedTarget) {
        return cachedTarget;
      }

      let programTarget;

      try {
        programTarget = await programTargetsDb.read(target);
        this.addProgramTarget({ program: ref.program, target: programTarget });
      } catch (err) {
        // seems that error occurs only when someone is trying to load hard-deleted targets
        console.error('Trying to load hard-deleted target', err);

        return null;
      }

      return programTarget;
    });

    return compact(await Promise.all(retVal));
  }, // getProgramTargets()

  /**
   * Read Targets from cache or database. Cache the items if they are read from database
   *
   * @param {Object} ref
   * @param {string} ref.program
   * @param {string} ref.target
   * @param {boolean} [ref.preventDuplicates]
   *
   * @returns {Promise<Object>}
   */
  async getProgramTarget(ref) {
    if (!ref.program || !ref.target) {
      return {};
    }
    const preventDuplicates = get(ref, 'preventDuplicates', false);

    let target;
    if (get(this.programTargetsCache, [ref.program, ref.target])) {
      target = get(this.programTargetsCache, [ref.program, ref.target]);
    } else {
      try {
        const programTargetsDb = new ProgramTargetsDB(ref.program);
        // if target is no longer exists in database, save object with id into cache to prevent further requests
        target = await programTargetsDb.read(ref.target) || { id: ref.target };
        this.addProgramTarget({ program: ref.program, target });
      } catch (err) {
        logEvent(err);
        console.error(err);
        return {};
      }
    }
    if (preventDuplicates) {
      const duplicates = chain(this.programTargetsCache)
        .get(ref.program)
        .filter({ label: get(target, 'label') })
        .sortBy('order')
        .value();
      if (size(duplicates) > 1) {
        const index = findIndex(duplicates, { id: ref.target });
        return {
          ...target,
          label: `${target.label} (${index + 1})`,
        };
      }
    }
    return target;
  }, // getProgramTarget()

  /**
   * Read all Targets from cache or database. Cache the items if they are read from database
   */
  async getAllProgramTargets(ref) {
    if (!ref.program) {
      return [];
    }

    if ((get(this.programTargetsPerProgramCache, ref.program) === 'all') && !ref?.forceReload) {
      return chain(this.programTargetsCache)
        .get(ref.program)
        .sortBy('order')
        .defaultTo([])
        .value();
    }
    try {
      const programTargetsDb = new ProgramTargetsDB(ref.program);
      const programTargets = await programTargetsDb.readAll();
      this.addProgramTargets({ programId: ref.program, targets: programTargets, replace: true });

      this.addProgramTargetsPerProgram({ program: ref.program, loadStatus: 'all' });
      return chain(programTargets)
        .sortBy('order')
        .defaultTo([])
        .value();
    } catch (err) {
      logEvent(err);
      console.error(err);

      return [];
    }
  }, // getAllProgramTargets()

  /**
   * Read all active Targets from cache or database. Cache the items if they are read from database
   */
  async getActiveProgramTargets(ref) {
    if (!ref.program) {
      return [];
    }
    if (['all', 'active'].includes(get(this.programTargetsPerProgramCache, ref.program))) {
      return chain(this.programTargetsCache)
        .get(ref.program)
        .sortBy('order')
        .defaultTo([])
        .value();
    }
    try {
      const programTargetsDb = new ProgramTargetsDB(ref.program);
      const programTargetActions = concat(
        programTargetsDb.readAll(
          [
            ['parentTarget', '==', null],
            ['status', 'not-in', ['waiting', 'closed', 'aborted', 'hold']],
          ],
        ),
        programTargetsDb.readAll([['parentTarget', '!=', null]]),
      );
      const programTargets = flatten(await Promise.all(programTargetActions));
      this.addProgramTargets({ programId: ref.program, targets: programTargets });

      this.addProgramTargetsPerProgram({ program: ref.program, loadStatus: 'active' });
      return chain(programTargets)
        .sortBy('order')
        .defaultTo([])
        .value();
    } catch (err) {
      logEvent(err);
      console.error(err);

      return [];
    }
  }, // getActiveProgramTargets()

  invalidateProgramTargetsCache({ program } = {}) {
    if (!program) {
      this.clearAllPrograms();
      return;
    }

    this.clearProgram({ program });
  }, // invalidateProgramTargetsCache()

  async setProgramTargetProperties({ programId, targetId, programTargetObj }) {
    if (!programId || !targetId || isEmpty(programTargetObj)) {
      return;
    }
    this.setProperties({ programId, targetId, programTargetObj });

    const programsDb = new ProgramsDB();

    const programTargetsDb = new ProgramTargetsDB(programId);
    return Promise.all([
      programTargetsDb.set(programTargetObj, targetId),
      programsDb.set({}, programId), // Touch the program to update the last modified date
    ]);
  }, // setProgramTargetProperties
};
